import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONTACT, TURNOVER_OPTIONS, SERVICES } from "@/lib/site";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company: "", mobile: "", city: "", turnover: "", loan_requirement: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const on = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast.error("Please enter your name and mobile number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact" });
      toast.success("Thank you! Our team will reach out to you shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please call or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-start">
        {/* Left info */}
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-4">09 — Get In Touch</p>
          <h2 className="font-display font-extrabold text-navy tracking-tight text-4xl md:text-5xl leading-[1.05]">
            Let&apos;s fund your next chapter.
          </h2>
          <p className="mt-5 text-ink/55 text-base md:text-lg max-w-md">
            Share a few details and our advisors will assess your eligibility and craft a funding plan tailored to your business.
          </p>

          <div className="mt-9 space-y-4">
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-surface text-brand group-hover:bg-brand group-hover:text-white transition-colors"><Phone className="w-5 h-5" /></span>
              <span className="font-semibold text-navy">{CONTACT.phone}</span>
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 group">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-surface text-emerald group-hover:bg-emerald group-hover:text-white transition-colors"><MessageCircle className="w-5 h-5" /></span>
              <span className="font-semibold text-navy">WhatsApp Us</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-surface text-brand group-hover:bg-brand group-hover:text-white transition-colors"><Mail className="w-5 h-5" /></span>
              <span className="font-semibold text-navy">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-surface text-navy"><MapPin className="w-5 h-5" /></span>
              <span className="font-semibold text-navy">{CONTACT.location}</span>
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="bg-surface rounded-3xl p-8 lg:p-10 border border-black/5 tick-shadow">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold text-navy">Name *</label>
                <Input data-testid="contact-input-name" value={form.name} onChange={on("name")} placeholder="Your full name" className="mt-2 h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand/50" />
              </div>
              <div>
                <label className="text-sm font-semibold text-navy">Company</label>
                <Input data-testid="contact-input-company" value={form.company} onChange={on("company")} placeholder="Business name" className="mt-2 h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand/50" />
              </div>
              <div>
                <label className="text-sm font-semibold text-navy">Mobile *</label>
                <Input data-testid="contact-input-mobile" value={form.mobile} onChange={on("mobile")} placeholder="+91 9XXXXXXXXX" className="mt-2 h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand/50" />
              </div>
              <div>
                <label className="text-sm font-semibold text-navy">City</label>
                <Input data-testid="contact-input-city" value={form.city} onChange={on("city")} placeholder="e.g. New Delhi" className="mt-2 h-12 rounded-xl bg-white border-black/10 focus-visible:ring-2 focus-visible:ring-brand/50" />
              </div>
              <div>
                <label className="text-sm font-semibold text-navy">Annual Turnover</label>
                <Select value={form.turnover} onValueChange={on("turnover")}>
                  <SelectTrigger data-testid="contact-input-turnover" className="mt-2 h-12 rounded-xl bg-white border-black/10"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{TURNOVER_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-navy">Loan Requirement</label>
                <Select value={form.loan_requirement} onValueChange={on("loan_requirement")}>
                  <SelectTrigger data-testid="contact-input-loan" className="mt-2 h-12 rounded-xl bg-white border-black/10"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-5">
              <label className="text-sm font-semibold text-navy">Message</label>
              <Textarea data-testid="contact-input-message" value={form.message} onChange={on("message")} placeholder="Tell us briefly about your funding needs" className="mt-2 rounded-xl bg-white border-black/10 min-h-[110px] focus-visible:ring-2 focus-visible:ring-brand/50" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 py-4 rounded-full bg-brand hover:bg-navy text-white font-semibold transition-colors disabled:opacity-60">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Book Free Consultation <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="mt-3 text-center text-xs text-ink/40">We respect your privacy. Your details are never shared.</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
